/**
 * Les joueurs virtuels : lecture des tables de décision calculées par `sim/`.
 *
 * On ne recalcule PAS la programmation dynamique en TypeScript. Ce serait une
 * seconde implémentation à maintenir et à prouver, alors que le résultat tient en
 * 5 Ko compressés. `sim/export_tables.py` produit le fichier, et le bloc de
 * vérification qu'il embarque permet à `tests/ia.test.ts` de prouver que ce
 * fichier est lu ET appliqué correctement.
 *
 * Le point délicat est là : la DP raisonne sur des VALEURS de dés (ils sont
 * interchangeables) tandis que le moteur agit sur des dés IDENTIFIÉS. La table est
 * donc indexée par main triée en décroissant, et il faut retraduire vers les
 * index réels du tapis — une erreur ici donnerait un jeu subtilement faux sans
 * jamais lever d'exception.
 */
import { evaluerMain } from "./combinaisons.js";
export class TablesIncompatibles extends Error {
}
/** Vérifie qu'un fichier de tables correspond bien aux règles en vigueur. */
export function chargerTables(brut, ruleSetId) {
    const t = brut;
    if (!t || typeof t !== "object" || !t.equilibre || !t.seuil) {
        throw new TablesIncompatibles("fichier de tables illisible");
    }
    if (t.ruleSetId !== ruleSetId) {
        // Changer la hiérarchie ou la valeur d'une combinaison invalide les tables :
        // mieux vaut refuser que jouer une politique calculée pour d'autres règles.
        throw new TablesIncompatibles(`tables calculées pour « ${t.ruleSetId} », règles en vigueur « ${ruleSetId} »`);
    }
    if (t.mains.length !== 56) {
        throw new TablesIncompatibles(`${t.mains.length} mains au lieu de 56`);
    }
    return t;
}
/**
 * Ordre canonique des dés : décroissant, comme dans la table. On conserve les
 * index d'origine pour pouvoir retraduire la décision vers le tapis réel.
 */
function trierDecroissant(main) {
    return main
        .map((valeur, index) => ({ index, valeur }))
        .sort((a, b) => b.valeur - a.valeur || a.index - b.index);
}
/** Décision brute lue dans la table, exprimée en positions canoniques. */
function lire(tables, main, relances, choix) {
    const cle = evaluerMain(main).cle;
    const table = "seuilScore" in choix
        ? tables.seuil[String(choix.seuilScore)]
        : tables.equilibre[choix.objectif]?.[String(Math.min(5, Math.max(1, choix.k)))];
    if (!table) {
        throw new TablesIncompatibles(`table absente pour ${JSON.stringify(choix)}`);
    }
    const parRelance = table[cle];
    if (!parRelance)
        throw new TablesIncompatibles(`main ${cle} absente de la table`);
    const decision = parRelance[relances - 1];
    if (!decision)
        throw new TablesIncompatibles(`relance ${relances} absente pour ${cle}`);
    return decision;
}
/**
 * Dés à garder, en index du tapis. Un tableau de trois index signifie « je
 * m'arrête » — c'est le même geste que garder tous les dés.
 */
export function gardeConseillee(tables, main, relances, choix) {
    const canonique = trierDecroissant(main);
    const positions = lire(tables, main, relances, choix);
    return positions.map((p) => canonique[p].index).sort((a, b) => a - b);
}
/** Valeurs des dés gardés, dans l'ordre décroissant : utile pour les tests. */
export function valeursGardees(tables, main, relances, choix) {
    return gardeConseillee(tables, main, relances, choix)
        .map((i) => main[i])
        .sort((a, b) => b - a);
}
/**
 * Traduit des VALEURS de dés à garder en index du tapis.
 *
 * C'est le seul endroit où un bug resterait muet : la décision serait juste et le dé
 * gardé faux, sans jamais lever d'exception. D'où une fonction isolée, et des tests
 * qui l'exercent sur les mains à doublons — `garder([2, 2])` est ambigu, `garder les
 * dés 0 et 2` ne l'est pas.
 */
export function valeursVersIndices(main, valeurs) {
    const restants = [...valeurs];
    const indices = [];
    main.forEach((valeur, index) => {
        const place = restants.indexOf(valeur);
        if (place >= 0) {
            restants.splice(place, 1);
            indices.push(index);
        }
    });
    if (restants.length > 0) {
        throw new Error(`garde [${valeurs.join(", ")}] impossible avec la main [${main.join(", ")}]`);
    }
    return indices;
}
/**
 * Objectif à poursuivre selon la phase.
 *
 * En charge il suffit de ne pas être le plus faible ; en décharge il faut être le
 * plus fort pour se vider. Confondre les deux coûte 6,1 points de taux de défaite
 * à quatre joueurs — c'est l'erreur la plus chère du 421 (sim/RAPPORT.md § 9).
 */
export function objectifDe(phase) {
    return phase === "CHARGE" ? "charge" : "decharge";
}
//# sourceMappingURL=ia.js.map